// Long-term memories are markdown files Flora writes under /memories/ with the
// generic filesystem tools, so a memory change is a write_file/edit_file/delete
// whose file_path sits under that mount (see the backend's
// flora_langchain/filesystem.py).
const MEMORY_FILE_WRITE_TOOLS: ReadonlySet<string> = new Set(['write_file', 'edit_file', 'delete']);
const MEMORIES_ROOT = '/memories';
// The reserved preferences file backs the "learned preferences" list of the
// user-preferences endpoint, so a write to it refreshes that cache instead.
const PREFERENCES_FILE_PATH = `${MEMORIES_ROOT}/preferences.md`;

type MemoryFileWriteKind = 'memories' | 'preferences';

/**
 * One socket's memory-write bookkeeping. `tool_end` / `tool_error` frames carry
 * no tool input or call id, so an end can't be matched to the start it belongs
 * to; instead the tracker remembers which caches the socket's in-flight writes
 * touch, and refreshes them on every successful end until all tracked calls
 * have settled.
 */
interface MemoryWriteTracker {
  /** Tracked calls started and not yet ended, per tool name. */
  inFlight: Record<string, number>;
  /** Caches touched by the current burst of in-flight writes. */
  touched: Set<MemoryFileWriteKind>;
}

/** The socket-frame fields memory-write bookkeeping reads. */
interface MemoryFileWriteFrame {
  is_tool_start?: unknown;
  is_tool_end?: unknown;
  is_tool_error?: unknown;
  tool_name?: unknown;
  tool_input?: unknown;
}

/**
 * Normalizes a model-supplied file path the way the backend's filesystem does
 * before it acts on it: relative paths are rooted at `/`, and empty, `.` and
 * `..` segments are resolved.
 *
 * @param {string} filePath - The raw `file_path` argument
 * @returns {string} The normalized absolute path
 *
 * @example
 * normalizeFilePath('memories/./notes.md'); // → '/memories/notes.md'
 */
const normalizeFilePath = (filePath: string): string => {
  const parts: string[] = [];
  for (const part of filePath.split('/')) {
    if (part === '' || part === '.') continue;
    if (part === '..') parts.pop();
    else parts.push(part);
  }
  return `/${parts.join('/')}`;
};

/**
 * Classifies a filesystem tool call by the caches its write affects.
 *
 * @param {unknown} toolName - The frame's `tool_name`
 * @param {unknown} toolInput - The frame's `tool_input`
 * @returns {MemoryFileWriteKind[]} The affected caches; empty for a call outside /memories
 *
 * @example
 * memoryFileWriteKinds('delete', { file_path: '/memories' }); // → ['memories', 'preferences']
 */
const memoryFileWriteKinds = (toolName: unknown, toolInput: unknown): MemoryFileWriteKind[] => {
  if (typeof toolName !== 'string' || !MEMORY_FILE_WRITE_TOOLS.has(toolName)) return [];
  const filePath = (toolInput as { file_path?: unknown } | null | undefined)?.file_path;
  if (typeof filePath !== 'string') return [];
  const path = normalizeFilePath(filePath);
  if (path === MEMORIES_ROOT) return ['memories', 'preferences'];
  if (path === PREFERENCES_FILE_PATH) return ['preferences'];
  if (path.startsWith(`${MEMORIES_ROOT}/`)) return ['memories'];
  return [];
};

/**
 * Creates the memory-write bookkeeping for one socket, so an abandoned run's
 * unsettled calls are dropped with its socket.
 *
 * @returns {MemoryWriteTracker} An empty tracker
 */
const createMemoryWriteTracker = (): MemoryWriteTracker => ({ inFlight: {}, touched: new Set() });

/**
 * Feeds one socket frame to the tracker and returns the caches to refresh now.
 * A successful end of any tracked call refreshes every cache the in-flight
 * burst touches — the refresh may come early for a write still running, but
 * it always also comes on (or after) that write's own end. A burst whose only
 * ends were failures refreshes nothing.
 *
 * @param {MemoryWriteTracker} tracker - The socket's tracker (mutated)
 * @param {MemoryFileWriteFrame} frame - The parsed socket frame
 * @returns {MemoryFileWriteKind[]} The caches to invalidate
 *
 * @example
 * trackMemoryWriteFrame(tracker, { is_tool_start: true, tool_name: 'write_file', tool_input: { file_path: '/memories/a.md' } }); // → []
 * trackMemoryWriteFrame(tracker, { is_tool_end: true, tool_name: 'write_file' }); // → ['memories']
 */
const trackMemoryWriteFrame = (tracker: MemoryWriteTracker, frame: MemoryFileWriteFrame): MemoryFileWriteKind[] => {
  const toolName = frame.tool_name;
  if (typeof toolName !== 'string' || !MEMORY_FILE_WRITE_TOOLS.has(toolName)) return [];
  if (frame.is_tool_start) {
    tracker.inFlight[toolName] = (tracker.inFlight[toolName] ?? 0) + 1;
    for (const kind of memoryFileWriteKinds(toolName, frame.tool_input)) tracker.touched.add(kind);
    return [];
  }
  const succeeded = Boolean(frame.is_tool_end);
  if (!succeeded && !frame.is_tool_error) return [];
  if (tracker.inFlight[toolName]) tracker.inFlight[toolName] -= 1;
  const toRefresh = succeeded ? [...tracker.touched] : [];
  if (Object.values(tracker.inFlight).every((count) => count === 0)) tracker.touched.clear();
  return toRefresh;
};

export { createMemoryWriteTracker, memoryFileWriteKinds, normalizeFilePath, trackMemoryWriteFrame };
export type { MemoryFileWriteFrame, MemoryFileWriteKind, MemoryWriteTracker };
